<script setup>
import { computed } from 'vue';

import HrTag from './HrTag.vue';

/**
 * Several tags read as one field's value.
 *
 * The list exists because the spacing is its job, not each tag's. Tags rendered straight into a
 * container touch, and a row of touching tags reads as one run-together string — which is what
 * happened to a table of imported interventions. Material 3 spaces a set 8dp apart and wraps it, so
 * that lives here once instead of at every call site.
 *
 * Takes the values rather than markup: a caller usually has an array of strings from a record and
 * should not have to write a `v-for` and pick a key to show them.
 *
 * @status recommended
 * @group chips
 * @useWhen Showing every value of a multi-value field — topics, categories, interventions
 * @useWhen A column too narrow to read two tags side by side — with `flow="stacked"`
 * @useWhen A container of fixed size that clips, with a tooltip — with `flow="single-line"`
 * @avoidWhen There is one value — use HrTag on its own
 * @avoidWhen The values are selectable or removable — use the chip family
 * @related HrTag
 * @a11y Renders a `ul`/`li`, so a screen reader announces how many values there are before reading
 *   them. Decorative bullets and indentation are removed.
 */
defineOptions({ name: 'HrTagList' });

const props = defineProps({
  /**
   * The values to show.
   * @type {string[]}
   * @example ["Modeling (MD)","Visual Supports (VS)","Naturalistic Intervention (NI)"]
   */
  tags: {
    type: Array,
    default: () => [],
  },
  /**
   * How the tags are arranged.
   *
   * `wrap` flows them across and on to the next line, which is right wherever the space can grow.
   * `single-line` keeps them on one row for a container of fixed size to clip — pair it with a
   * tooltip, or the values past the edge are simply gone. `stacked` puts one per line, for a column
   * too narrow to read two side by side.
   *
   * A fact about the space the caller has, which this component cannot see.
   * @values wrap, single-line, stacked
   * @example single-line
   */
  flow: {
    type: String,
    default: 'wrap',
    validator: (value) => ['wrap', 'single-line', 'stacked'].includes(value),
  },
});

const FLOWS = ['wrap', 'single-line', 'stacked'];

/** A validator only warns, so an unsupported value would otherwise render an unstyled list. */
const resolvedFlow = computed(() => (FLOWS.includes(props.flow) ? props.flow : 'wrap'));

const entries = computed(() => props.tags
  .filter((tag) => tag !== null && tag !== undefined && tag !== '')
  .map(String));

</script>

<template>
  <ul
    v-if="entries.length > 0"
    :class="['hr-tag-list', `hr-tag-list--${resolvedFlow}`]"
  >
    <li
      v-for="(tag, index) in entries"
      :key="`${index}:${tag}`"
      class="hr-tag-list__item"
    >
      <!-- Index in the key because a field may legitimately hold the same value twice; the label
           alone would collide and Vue would reuse the wrong node. -->
      <hr-tag :label="tag" />
    </li>
  </ul>
</template>

<style lang="scss" scoped>
.hr-tag-list {
  display: flex;
  gap: var(--app-comp-tag-list-gap);
  margin: 0;
  padding: 0;
  list-style: none;
}

.hr-tag-list--wrap {
  flex-wrap: wrap;
}

// Nothing is dropped and nothing is counted: the row runs past its container, which is what makes a
// tooltip the other half of this and not an optional extra.
.hr-tag-list--single-line {
  flex-wrap: nowrap;
}

// align-items rather than the default stretch, so a stacked tag stays its own width instead of
// growing to the widest one beside it.
.hr-tag-list--stacked {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: flex-start;
}

.hr-tag-list__item {
  display: inline-flex;
}
</style>
